import type { PhotoMetadataRecord, Rating } from "@/lib/catalog"
import { decodeApertureValue, decodeShutterSpeedValue } from "@/core/exif/exif"
import { EmbeddedMetadata } from "@/lib/metadata/metadata-loader.thread.worker"
import { clamp } from "./math"
import { getWBFromExtractedMetadata } from "@/lib/raw/camera-utils"
import { getSidecarColorTag, getSidecarFlag, getSidecarRating } from "./xmp"
import { getFileExtension, getFileId } from "./file"
import { normalizeLensName } from "@/lib/metadata/exif/lens-name-utils"
import { RationalUtils } from "@/lib/metadata/core/binary-utils"

export const getPhotoMetadataRecordFromRawMetadata = async (
	file: File,
	{
		metadata: {
			easyaccess = {},
			lensCorrectionsExif = {},
			exif = {},
			xmp,
			colorCorrectionsExif,
		},
		sidecarMetadata,
	}: EmbeddedMetadata,
): Promise<PhotoMetadataRecord> => {
	const dateCreated = easyaccess["DateTimeOriginal"]

	const dateCreatedOffset =
		exif["Exif.Image.OffsetTimeOriginal"] ??
		exif["Exif.Photo.OffsetTimeOriginal"] ??
		exif["Exif.Image.OffsetTimeDigitized"] ??
		exif["Exif.Photo.OffsetTimeDigitized"] ??
		exif["Exif.Image.OffsetTime"] ??
		exif["Exif.Photo.OffsetTime"]
	const dateCreatedSubSec =
		exif["Exif.Photo.SubSecTimeOriginal"] ??
		exif["Exif.Photo.SubSecTimeDigitized"] ??
		exif["Exif.Photo.SubSecTime"]

	const dateModified = new Date(file.lastModified)

	// Was getExifOrientation, but easyaccess has it
	const orientation = easyaccess["Orientation"]

	let width =
		exif["Exif.SubImage1.ImageWidth"] ??
		exif["Exif.SubImage2.ImageWidth"] ??
		exif["Exif.Image.ImageWidth"]
	let height =
		exif["Exif.SubImage1.ImageLength"] ??
		exif["Exif.SubImage2.ImageLength"] ??
		exif["Exif.Image.ImageLength"]

	// Apply orientation to dimensions
	// Orientations 5, 6, 7, 8 require width/height swap (90° or 270° rotations)
	if (
		orientation &&
		(orientation === 5 || orientation === 6 || orientation === 7 || orientation === 8)
	) {
		const temp = width
		width = height
		height = temp
	}

	// Image info
	const iso = easyaccess["ISOspeed"]

	const apertureValue = parseApertureValue(
		exif["Exif.Image.ApertureValue"] ?? exif["Exif.Photo.ApertureValue"],
	)
	const fNumber = RationalUtils.toNumber(easyaccess["FNumber"]) ?? apertureValue

	const shutterSpeedValue = parseShutterSpeedValue(
		exif["Exif.Image.ShutterSpeedValue"] ?? exif["Exif.Photo.ShutterSpeedValue"],
	)
	const exposureTime = RationalUtils.toNumber(easyaccess["ExposureTime"]) ?? shutterSpeedValue

	// Lens info
	const focalLength = easyaccess["FocalLength"]
	const focalLength35mm = exif["Exif.Photo.FocalLengthIn35mmFilm"]

	// Camera info
	const cameraMake: string | undefined = easyaccess["Make"]
	const cameraModel: string | undefined = easyaccess["Model"]
	const cameraSerialNumber = easyaccess["SerialNumber"]

	const lensMake = normalizeLensName(exif["Exif.Photo.LensMake"]) ?? ""
	// Raw maker-note LensID values are lookup keys, not user-facing lens labels.
	// Do not display Exif.NikonLd*.LensID*, Exif.Sony2.LensID, etc. unless a
	// parser resolves them to a human-readable string such as Exif.Nikon3.Lens.
	const lensModel =
		normalizeLensName(easyaccess["LensName"]) ??
		normalizeLensName(exif["Exif.CanonCs.LensType"]) ??
		normalizeLensName(exif["Exif.Photo.LensModel"]) ??
		normalizeLensName(exif["Exif.Canon.LensModel"]) ??
		normalizeLensName(exif["Exif.OlympusEq.LensType"]) ??
		normalizeLensName(exif["Exif.OlympusEq.LensModel"]) ??
		normalizeLensName(exif["Exif.Nikon3.Lens"]) ??
		normalizeLensName(exif["Exif.Panasonic.LensType"]) ??
		"Unknown"

	// Other
	const flash = easyaccess["Flash"]
	const artist = exif["Exif.Image.Artist"]
	const copyright = exif["Exif.Image.Copyright"]

	// Rating
	const rating = getSidecarRating(sidecarMetadata) ?? getExifRating(xmp)

	// Color tag
	const colorTag = getSidecarColorTag(sidecarMetadata) ?? getExifColorTag(xmp)

	// Flag
	const flag = getSidecarFlag(sidecarMetadata)

	// Black/White levels
	const whiteLevel = RationalUtils.toNumber(exif["Exif.SubImage1.WhiteLevel"])
	const blackLevel = RationalUtils.toNumber(exif["Exif.SubImage1.BlackLevel"])

	// Auxiliary data (DNG, lens correction, etc)
	const auxiliaryData = {}

	const baselineExposure = RationalUtils.toNumber(exif["Exif.Image.BaselineExposure"])

	if (baselineExposure != null) {
		auxiliaryData["baselineExposure"] = baselineExposure
	}

	// Adding all lens correction parameters automatically
	for (const key in lensCorrectionsExif) {
		auxiliaryData[key] = lensCorrectionsExif[key]
	}

	// White balance is auxiliary metadata: profile lookup or matrix parsing can fail even when
	// core EXIF fields are valid, so keep Make/Model/Lens extraction from falling back to Unknown.
	try {
		const wb = await getWBFromExtractedMetadata({
			easyaccess: easyaccess ?? {},
			exif: exif ?? {},
			xmp: xmp ?? {},
			lensCorrectionsExif: lensCorrectionsExif ?? {},
			colorCorrectionsExif: colorCorrectionsExif ?? {},
		})
		if (wb) {
			auxiliaryData["whiteBalanceInfo"] = wb.toInfo()
		}
	} catch (error) {
		console.warn("Failed to compute white balance from extracted metadata:", error)
	}

	return {
		isComplete: true,
		photoId: getFileId(file),
		extension: getFileExtension(file.name),
		size: file.size,
		hasEmbeddedPreview: null,
		dateCreated: dateCreated
			? convertExifStringToDate(dateCreated, dateCreatedOffset, dateCreatedSubSec)
			: null,
		dateCreatedOffset: dateCreatedOffset ? convertExifOffsetToMinutes(dateCreatedOffset) : null,
		dateModified: dateModified.getTime(),
		width: width ?? null,
		height: height ?? null,
		orientation: orientation ?? null,
		iso: iso ?? null,
		focalLength: RationalUtils.toNumber(focalLength) ?? null,
		focalLength35mm: focalLength35mm,
		focusDistance: easyaccess["SubjectDistance"] ?? 0,
		shutterSpeed: exposureTime ?? null,
		fNumber: fNumber ?? null,
		lensMake: lensMake ?? null,
		lensModel: lensModel ?? null,
		cameraMake: cameraMake ?? null,
		cameraModel: cameraModel ?? null,
		cameraSerialNumber: cameraSerialNumber ?? null,
		flash: flash ?? null,
		rating: rating ?? null,
		artist: artist ?? null,
		copyright: copyright ?? null,
		colorTag: colorTag ?? null,
		flag: flag ?? "unflagged",
		whiteLevel: whiteLevel ?? null,
		blackLevel: blackLevel ?? null,
		auxiliaryData,
	}
}

export const convertDateOffsetToExifString = (offsetMinutes: number) => {
	const hours = Math.floor(Math.abs(offsetMinutes) / 60)
	const minutes = Math.abs(offsetMinutes) % 60

	const hoursStr = hours.toString().padStart(2, "0")
	const minutesStr = minutes.toString().padStart(2, "0")

	return offsetMinutes < 0 ? `-${hoursStr}:${minutesStr}` : `+${hoursStr}:${minutesStr}`
}

export const convertExifOffsetToMinutes = (offset?: string) => {
	if (!offset) {
		return 0
	}

	// Parse offset
	const offsets = offset.split(":")
	const hoursOffset = Number(offsets[0] ?? 0)
	const minutesOffset = Number(offsets[1] ?? 0)
	const sign = Math.sign(hoursOffset)

	return (Math.abs(hoursOffset) * 60 + minutesOffset) * sign
}

export const convertDateToExifString = (dateObject: Date, offsetMinutes = 0) => {
	// Apply timezone offset (convert UTC timestamp to local time with offset)
	const adjustedDate = new Date(dateObject.getTime())
	adjustedDate.setMinutes(adjustedDate.getMinutes() + offsetMinutes)

	// e.g. '1970-01-01T00:00:00.000Z'
	let string = adjustedDate.toISOString()

	// Strip timezone offset
	// e.g. '1970-01-01T00:00:00'
	string = string.split(".")[0] as string

	// Replace dashes with colons
	// e.g. '1970:01:01T00:00:00'
	string = string.replace(/-/g, ":")

	// Separate date and time
	// e.g. '1970:01:01 00:00:00'
	return string.replace(/T/g, " ")
}

export const convertExifStringToDate = (
	timestamp?: string,
	offset?: string,
	subSeconds?: string,
): number => {
	if (!timestamp) {
		return new Date().getTime()
	}

	const minutesOffset = convertExifOffsetToMinutes(offset)

	// Parse datetime
	const [datePart, timePart] = timestamp.split(" ")
	if (!datePart || !timePart) {
		return new Date().getTime()
	}
	const [year, month, day] = datePart.split(":")
	const [hours, minutes, seconds] = timePart.split(":")
	const [secondWholePart, secondFractionPart] = (seconds ?? "").split(".")
	const normalizedSubSecond = `${subSeconds ?? ""}`.trim().replace(/\D/g, "")
	const fractionalDigits = normalizedSubSecond || (secondFractionPart ?? "")
	const milliseconds =
		fractionalDigits.length > 0 ? Number(`${fractionalDigits}`.slice(0, 3).padEnd(3, "0")) : 0

	const date = Date.UTC(
		Number(year),
		Number(month) - 1,
		Number(day),
		Number(hours),
		Number(minutes) - minutesOffset,
		Number(secondWholePart),
		milliseconds,
	)

	return date
}

export const getExifOrientation = (
	exif: EmbeddedMetadata["metadata"]["exif"],
): Orientation | null => {
	if (!exif) {
		return null
	}

	//  -1 = n/a
	//  0 = None
	//  1 = Rotate 90 CW
	//  2 = Rotate 180
	//  3 = Rotate 270 CW

	const autoRotate = exif["Exif.CanonSi.AutoRotate"]
	const orientation: Orientation | undefined = exif["Exif.Image.Orientation"]

	switch (autoRotate) {
		case 1:
			return 6
		case 2:
			return 3
		case 3:
			return 8
	}

	return orientation ?? null
}

export const cleanUpRating = (ratingString: string): Rating | null => {
	const rating = parseInt(ratingString, 10)

	if (isNaN(rating)) {
		return null
	}

	// 0-5
	return clamp(rating, 0, 5) as Rating
}

export const getExifRating = (xmp: EmbeddedMetadata["metadata"]["xmp"]): Rating | null => {
	if (!xmp) {
		return null
	}

	return cleanUpRating(xmp["Xmp.xmp.Rating"])
}

export const getExifColorTag = (xmp: EmbeddedMetadata["metadata"]["xmp"]): ColorTag | null => {
	if (!xmp) {
		return null
	}

	const colorTag = xmp["Xmp.xmp.Label"]
	if (isColorTag(colorTag)) {
		return colorTag as ColorTag
	} else {
		return null
	}
}

export interface RationalNumber {
	numerator: number
	denominator: number
}

export const parseShutterSpeedValue = (rational?: RationalNumber | number) => {
	const value = RationalUtils.toNumber(rational)

	if (value != null) {
		return decodeShutterSpeedValue(value)
	}

	return value
}

export const parseApertureValue = (rational?: RationalNumber | number) => {
	const value = RationalUtils.toNumber(rational)

	if (value != null) {
		return decodeApertureValue(value)
	}

	return value
}

/**
 * See: https://exiftool.org/TagNames/EXIF.html#Flash
 */
export const didFlashFire = (flash: number | null) => {
	if (flash == null) {
		return false
	}

	switch (flash) {
		case 0x0:
			return false // No Flash
		case 0x1:
			return true // Fired
		case 0x5:
			return true // Fired, Return not detected
		case 0x7:
			return true // Fired, Return detected
		case 0x8:
			return false // On, Did not fire
		case 0x9:
			return true // On, Fired
		case 0xd:
			return false // On, Return not detected
		case 0xf:
			return false // On, Return detected
		case 0x10:
			return false // Off, Did not fire
		case 0x14:
			return false // Off, Did not fire, Return not detected
		case 0x18:
			return false // Auto, Did not fire
		case 0x19:
			return true // Auto, Fired
		case 0x1d:
			return true // Auto, Fired, Return not detected
		case 0x1f:
			return true // Auto, Fired, Return detected
		case 0x20:
			return false // No flash function
		case 0x30:
			return false // Off, No flash function
		case 0x41:
			return true // Fired, Red-eye reduction
		case 0x45:
			return true // Fired, Red-eye reduction, Return not detected
		case 0x47:
			return true // Fired, Red-eye reduction, Return detected
		case 0x49:
			return false // On, Red-eye reduction
		case 0x4d:
			return false // On, Red-eye reduction, Return not detected
		case 0x4f:
			return false // On, Red-eye reduction, Return detected
		case 0x50:
			return false // Off, Red-eye reduction
		case 0x58:
			return false // Auto, Did not fire, Red-eye reduction
		case 0x59:
			return true // Auto, Fired, Red-eye reduction
		case 0x5d:
			return true // Auto, Fired, Red-eye reduction, Return not detected
		case 0x5f:
			return true // Auto, Fired, Red-eye reduction, Return detected
		default:
			return false // Unknown
	}
}

export const NO_COLOR_TAG = "No Color"
export const ColorTagValues = ["Red", "Yellow", "Green", "Blue", "Purple", NO_COLOR_TAG] as const
export type ColorTag = (typeof ColorTagValues)[number]
export const isColorTag = (value: any) => {
	return ColorTagValues.includes(value)
}
export const isRealColorTag = (value?: ColorTag | null): value is ColorTag => {
	return value != null && value !== NO_COLOR_TAG
}

export const getColorFromShortcut = (key: string) => {
	switch (key) {
		case "6":
			return "Red"
		case "7":
			return "Yellow"
		case "8":
			return "Green"
		case "9":
			return "Blue"
		case "mod+0":
			return "No Color"
	}
}

export const CullingValues = ["Keeper", "Candidate", "Reject"] as const

export type CullingTag = (typeof CullingValues)[number]

export const getCullingTagLabel = (tag: CullingTag) => {
	return tag === "Keeper" ? "Top Pick" : tag
}

export const isCullingTag = (value: any) => {
	return CullingValues.includes(value)
}

const OrientationValues = [1, 2, 3, 4, 5, 6, 7, 8] as const
export type Orientation = (typeof OrientationValues)[number]
export const isOrientation = (value: any) => {
	return OrientationValues.includes(value)
}

// --- EXIF Rational array converters ---

import type { Rational } from "@/lib/metadata/core/types"

export function rationalsToFloats(rationals: Rational[]): Float32Array {
	return new Float32Array(rationals.map((rational) => RationalUtils.toDecimal(rational)))
}

export function resolveColorMatrix(
	exif: Rational[] | undefined,
	profileMatrix: number[] | undefined,
): Float32Array {
	if (exif) return rationalsToFloats(exif)
	if (profileMatrix) return new Float32Array(profileMatrix)
	return new Float32Array(9)
}
